import React, { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { useTranslation } from "react-i18next";
import {
  createProsecutorAssignment,
  updateProsecutorAssignment,
  deleteProsecutorAssignment,
} from "services/prosecutorAssignentService";
import { getEmployeesByAdminAndOrg } from "services/getEmployees";
import { getLegalResearchRequest } from "services/legalResearchService";
import { getInitials } from "utils/helpers";
import {
  X,
  Trash2,
  Award,
  Shield,
  UserPlus,
  Search,
  RefreshCw,
} from "lucide-react";

interface TeamMember {
  id: number;
  name: string;
  role: string;
  isTeamLeader: boolean;
  prosecutor_name?: string;
  is_team_leader?: boolean;
  department?: string;
  prosecutor_user_id?: number;
}

interface EmployeeData {
  user: {
    id: number;
    first_name: string;
    last_name?: string;
  };
  roles: string[];
  department?: string;
}

interface EditTeamModalProps {
  request: any;
  teamId: number;
  isOpen: boolean;
  onClose: () => void;
}

const EditTeamModal: React.FC<EditTeamModalProps> = ({
  request,
  teamId,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const contextData = useAuth();
  const { showToast } = useToast();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [availableEmployees, setAvailableEmployees] = useState<EmployeeData[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeData | null>(null);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // Get the specific team from research_teams (internal) or prosecutor_assignments (external)
  useEffect(() => {
    if (isOpen) {
      // For internal requests, use research_teams
      if (request.requested_by === "internal" && request.research_teams && request.research_teams[teamId]) {
        const team = request.research_teams[teamId];
        setTeamMembers(
          (team.prosecutors ?? []).map((member: any) => ({
            ...member,
            isTeamLeader: member.is_team_leader ?? false,
            id: member.id || member.prosecutor_user_id,
            name: member.prosecutor_name || member.name,
            role: "Prosecutor",
            department: member.department || t("LegalDepartment"),
          }))
        );
      }
      // For external requests, use prosecutor_assignments
      else if (request.requested_by === "external" && request.prosecutor_assignments) {
        setTeamMembers(
          request.prosecutor_assignments.map((member: any) => ({
            ...member,
            isTeamLeader: member.is_team_leader ?? false,
            id: member.id || member.prosecutor_user_id,
            name: member.prosecutor_name || member.name,
            role: "Prosecutor",
            department: member.department || t("LegalDepartment"),
          }))
        );
      }
    }
  }, [isOpen, teamId, request, t]);

  // Fetch available employees when add member is shown
  useEffect(() => {
    if (showAddMember && isOpen) {
      setLoadingEmployees(true);
      getEmployeesByAdminAndOrg({
        bearer: contextData.token || contextData?.user?.access_token || "",
        admin_unit: contextData?.user?.admin_unit || "",
        org_unit: contextData?.user?.org_unit || "",
      })
        .then((data: EmployeeData[]) => {
          // Filter to only prosecutors
          const prosecutors = data.filter((emp) =>
            emp.roles?.some((role: string) => role === "LegalResearchProsecutor")
          );
          
          // Get current team member IDs
          const currentMemberIds = teamMembers.map((m) => m.id);
          
          // Filter out employees already in the team
          const available = prosecutors.filter(
            (emp) => !currentMemberIds.includes(emp.user.id)
          );
          
          setAvailableEmployees(available);
        })
        .catch((error) => {
          console.error("Error fetching employees:", error);
          showToast(t("FailedToLoadAvailableProsecutors"), "error");
        })
        .finally(() => {
          setLoadingEmployees(false);
        });
    }
  }, [showAddMember, isOpen, teamMembers, contextData]);

  // Refresh the page data
  const refreshData = async () => {
    try {
      const response = await getLegalResearchRequest(request.id, contextData.token);
      // Trigger a custom event or use localStorage to notify parent to refresh
      window.dispatchEvent(new CustomEvent('refreshLegalResearch', { detail: response }));
    } catch (error) {
      console.error("Error refreshing data:", error);
    }
  };

  const handleSetTeamLeader = async (member: TeamMember) => {
    setSubmitting(true);
    try {
      // First, remove team leader status from all current team members
      const currentTeamLeader = teamMembers.find(m => m.isTeamLeader);
      if (currentTeamLeader && currentTeamLeader.id !== member.id) {
        let prevAssignmentId: string | null = null;

        if (currentTeamLeader.id) {
          prevAssignmentId = String(currentTeamLeader.id);
        }

        if (!prevAssignmentId) {
          const matchingAssignment = request.prosecutor_assignments?.find(
            (p: any) => p.prosecutor_user_id === currentTeamLeader.id
          );
          prevAssignmentId = matchingAssignment?.id ? String(matchingAssignment.id) : null;
        }

        if (prevAssignmentId) {
          const removeFormData = new FormData();
          removeFormData.append("is_team_leader", "false");
          await updateProsecutorAssignment(prevAssignmentId, removeFormData);
        }
      }

      // Now set the new team leader
      let assignmentId: string | null = null;

      if (member.id) {
        assignmentId = String(member.id);
      }

      if (!assignmentId) {
        const matchingAssignment = request.prosecutor_assignments?.find(
          (p: any) => p.prosecutor_user_id === member.id
        );
        assignmentId = matchingAssignment?.id ? String(matchingAssignment.id) : null;
      }

      if (assignmentId) {
        const formData = new FormData();
        formData.append("is_team_leader", "true");
        await updateProsecutorAssignment(assignmentId, formData);
      }

      // Update local state - ensure only one team leader
      setTeamMembers((prev) =>
        prev.map((m) => ({
          ...m,
          isTeamLeader: m.id === member.id,
        }))
      );

      showToast(t("TeamLeaderUpdatedSuccess"), "success");
      await refreshData();
    } catch (error) {
      console.error("Error updating team leader:", error);
      showToast(t("FailedToUpdateTeamLeader"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveMember = async (member: TeamMember) => {
    if (!window.confirm(t("ConfirmRemoveTeamMember"))) {
      return;
    }

    setSubmitting(true);
    try {
      // Check if member has its own ID
      let assignmentId: string | null = null;

      if (member.id) {
        assignmentId = String(member.id);
      }

      // Try to find matching assignment if no direct ID
      if (!assignmentId) {
        const matchingAssignment = request.prosecutor_assignments?.find(
          (p: any) => p.prosecutor_user_id === member.id
        );
        assignmentId = matchingAssignment?.id ? String(matchingAssignment.id) : null;
      }

      if (assignmentId) {
        await deleteProsecutorAssignment(assignmentId);
      }
      
      setTeamMembers((prev) => prev.filter((m) => m.id !== member.id));
      showToast(t("TeamMemberRemovedSuccess"), "success");
      await refreshData();
    } catch (error) {
      console.error("Error removing team member:", error);
      showToast(t("FailedToRemoveTeamMember"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddMember = async () => {
    if (!selectedEmployee) {
      showToast(t("PleaseSelectTeamMember"), "error");
      return;
    }

    setSubmitting(true);
    try {
      const fullName = `${selectedEmployee.user.first_name} ${selectedEmployee.user.last_name || ""}`.trim();
      
      const formData = new FormData();
      formData.append("prosecutor_user_id", String(selectedEmployee.user.id));
      formData.append("prosecutor_name", fullName);
      formData.append("research_request", String(request.id));
      formData.append("is_team_leader", "false");
      if (selectedEmployee.department) {
        formData.append("department", selectedEmployee.department);
      }

      // Check if this is an internal request - add as research team member
      if (request.requested_by === "internal") {
        formData.append("team_type", "prosecutor_topic_team");
        formData.append("research_team", String(request.research_teams?.[teamId]?.id));
      }

      await createProsecutorAssignment(formData);

      // Add to local state
      const newMember: TeamMember = {
        id: selectedEmployee.user.id,
        name: fullName,
        role: "Prosecutor",
        isTeamLeader: false,
        department: selectedEmployee.department || t("LegalDepartment"),
      };
      setTeamMembers((prev) => [...prev, newMember]);
      setShowAddMember(false);
      setSelectedEmployee(null);
      setSearchQuery("");
      showToast(t("TeamMemberAddedSuccess"), "success");
      await refreshData();
    } catch (error) {
      console.error("Error adding team member:", error);
      showToast(t("FailedToAddTeamMember"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const team = request.research_teams?.[teamId];
  
  // Filter employees based on search
  const filteredEmployees = availableEmployees.filter((emp) => {
    const fullName = `${emp.user.first_name} ${emp.user.last_name || ""}`.trim().toLowerCase();
    return fullName.includes(searchQuery.toLowerCase());
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {t("EditTeam")} - {team?.team_name || `${t("Team")} ${teamId + 1}`}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          {/* Add Member Section */}
          {showAddMember ? (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-3">{t("AddNewTeamMember")}</h3>
              
              {loadingEmployees ? (
                <div className="text-center py-4 text-gray-500">{t("Loading")}</div>
              ) : (
                <>
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder={t("SearchProsecutors")}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div className="max-h-40 overflow-y-auto mb-3 border rounded-lg">
                    {filteredEmployees.length === 0 ? (
                      <div className="p-3 center text-gray-500 text-sm">
                        {t("NoAvailableProsecutorsFound")}
                      </div>
                    ) : (
                      filteredEmployees.map((emp) => (
                        <div
                          key={emp.user.id}
                          onClick={() => setSelectedEmployee(emp)}
                          className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-100 ${
                            selectedEmployee?.user.id === emp.user.id
                              ? "bg-green-50"
                              : ""
                          }`}
                        >
                          <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-xs font-medium">
                            {getInitials(`${emp.user.first_name} ${emp.user.last_name || ""}`)}
                          </div>
                          <div>
                            <p className="font-medium text-sm">
                              {emp.user.first_name} {emp.user.last_name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {emp.department || t("LegalDepartment")}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setShowAddMember(false);
                        setSelectedEmployee(null);
                        setSearchQuery("");
                      }}
                      className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      {t("Cancel")}
                    </button>
                      <button
                        onClick={handleAddMember}
                        disabled={!selectedEmployee || submitting}
                        className="flex-1 py-2 px-4 bg-[#212121] text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 flex items-center justify-center"
                      >
                        {submitting ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {t("Adding")}
                          </>
                        ) : t("AddMember")}
                      </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="flex justify-end mb-3">
              <button
                onClick={() => setShowAddMember(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <UserPlus className="w-4 h-4" />
                {t("AddMember")}
              </button>
            </div>
          )}

          {/* Team Members List */}
          {teamMembers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {t("NoTeamMembersFound")}
            </div>
          ) : (
            <div className="space-y-3">
              {teamMembers.map((member) => (
                <div
                  key={member.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    member.isTeamLeader
                      ? "border-green-500 bg-green-50"
                      : "border-gray-200"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-sm font-medium">
                      {getInitials(member.name)}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{member.name}</p>
                      <p className="text-sm text-gray-500">
                        {member.department || t("LegalDepartment")}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Team Leader Badge */}
                    {member.isTeamLeader && (
                      <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                        <Award className="w-3 h-3" />
                        {t("TeamLeader")}
                      </span>
                    )}

                    {/* Set as Team Leader Button */}
                    {!member.isTeamLeader && (
                      <button
                        onClick={() => handleSetTeamLeader(member)}
                        disabled={submitting}
                        className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title={t("SetAsTeamLeader")}
                      >
                        <Shield className="w-4 h-4" />
                      </button>
                    )}

                    {/* Remove Member Button */}
                    <button
                      onClick={() => handleRemoveMember(member)}
                      disabled={submitting}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title={t("RemoveMember")}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50">
          <button
            onClick={async () => {
              await refreshData();
              onClose();
            }}
            className="w-full py-2 px-4 bg-[#212121] text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            {t("CloseAndRefresh")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditTeamModal;